// C:\Users\ADMIN\Desktop\ERP\myerp\my-frontend\src\pages\income\env\total\IncomeEnvTotalNew.jsx
import React, { useEffect, useMemo, useState } from "react";
import styles from "../disinfection/IncomeDraftCreate.module.css";
import api from "../../../../api";
import { useTabActivated } from "../../../../tabs/TabActivation";

import InvoiceForm from "./InvoiceForm";
import StatementForm from "./StatementForm";



const PAY_METHOD_LABELS = ["계좌이체", "카드결제", "현금결제"];

const PAY_METHOD_TO_CODE = { "계좌이체": "transfer", "카드결제": "card", "현금결제": "cash" };


const SUBCATEGORY_OPTIONS = [
  "냉난방기 종합세척",
  "승차대 청소",
  "간판 청소",
  "모래 소독",
  "논슬립 시공",
  "공기청정기 분해세척",
  "보도육교 승강기 청소",
  "입주 청소"
];

export default function IncomeEnvTotalNew({ fixedCategory = "환경/종합" }) {
  const [activeTab, setActiveTab] = useState("draft");
  const [category] = useState(fixedCategory);
  const [subcategory, setSubcategory] = useState("냉난방기 종합세척");
  const [draftDate, setDraftDate] = useState(() => toISO(new Date()));
  const [claimDate, setClaimDate] = useState(() => toISO(new Date()));
  const [workMonth, setWorkMonth] = useState("");
  const [amount, setAmount] = useState("");
  const [desc, setDesc] = useState("");
  const [title, setTitle] = useState("");
  const [seq, setSeq] = useState("001");
  const [loadingSeq, setLoadingSeq] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState(null);

  // ✅ 거래처 관련 상태
  const [vendors, setVendors] = useState([]);
  const [vendorInput, setVendorInput] = useState("");
  const [vendorId, setVendorId] = useState(null);
  const [showVendorList, setShowVendorList] = useState(false);
  const [loadingVendors, setLoadingVendors] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);

  const [payMethodLabel, setPayMethodLabel] = useState("계좌이체");

  // ✅ 냉난방기 종합세척 상세내역
  const [aircondDetails, setAircondDetails] = useState([
    { aircond_type: "벽걸이형", quantity: 0, unit_price: 0, contract_amount: 0, isDefault: true },
    { aircond_type: "스탠드형", quantity: 0, unit_price: 0, contract_amount: 0, isDefault: true },
    { aircond_type: "1way형",   quantity: 0, unit_price: 0, contract_amount: 0, isDefault: true },
    { aircond_type: "2way형",   quantity: 0, unit_price: 0, contract_amount: 0, isDefault: true },
    { aircond_type: "4way형",   quantity: 0, unit_price: 0, contract_amount: 0, isDefault: true },
  ]);


  // ✅ 상세행 값 변경 (계약금액 = 대수 × 단가 자동계산)
  const handleAircondChange = (index, field, rawValue) => {
    setAircondDetails((prev) => {
      const next = [...prev];
      if (field === "aircond_type") {
        next[index] = { ...next[index], aircond_type: rawValue };
      } else {
        const n = Number(String(rawValue).replace(/[^\d]/g, "")) || 0;
        next[index] = { ...next[index], [field]: n };
        if (field === "quantity" || field === "unit_price") {
          next[index].contract_amount =
            (next[index].quantity || 0) * (next[index].unit_price || 0);
        }
      }
      return next;
    });
  };

  // ✅ 행 추가
  const handleAddAircondRow = () => {
    setAircondDetails((prev) => [
      ...prev,
      { aircond_type: "", quantity: 0, unit_price: 0, contract_amount: 0 },
    ]);
  };

  // ✅ 행 삭제
  const handleRemoveAircondRow = (index) => {
    setAircondDetails((prev) => prev.filter((_, i) => i !== index));
  };

  // ✅ 상세 합계


  const aircondTotal = useMemo(
    () => aircondDetails.reduce((sum, d) => sum + (d.contract_amount || 0), 0),
    [aircondDetails]
  );

  // ✅ 냉난방기 선택 시 합계를 금액 필드에 자동반영
  useEffect(() => {
    if (subcategory === "냉난방기 종합세척") {
      setAmount(String(aircondTotal));
    }
  }, [aircondTotal, subcategory]);

  const yymmdd = useMemo(() => draftDate.replaceAll("-", "").slice(2), [draftDate]);
  


  const draftNo = useMemo(
    () => `금천일자리(주)${category}-${yymmdd}-${seq}호`,
    [category, yymmdd, seq]
  );

  // ✅ 여기에 추가! (54번째 줄)
  useEffect(() => {
    setClaimDate(draftDate);
  }, [draftDate]);

  // ✅ seq 자동 생성
  // ✅ keep-alive 탭 재방문 시 다음 번호를 다시 조회(마운트가 유지돼 처음 조회값에 멈춰 있지 않게)
  const [seqRefreshTick, setSeqRefreshTick] = useState(0);
  useTabActivated(() => setSeqRefreshTick((t) => t + 1));
  useEffect(() => {
    let ignore = false;
    async function getSeq() {
      if (!draftDate) return;
      setLoadingSeq(true);
      try {
        const { data } = await api.get(`/env-total-incomes/next-seq/?date=${draftDate}`);
        if (!ignore) setSeq(String(data.next_seq).padStart(3, "0"));
      } catch (e) {
        console.error("[next-seq] failed:", e?.response?.status);
      } finally {
        if (!ignore) setLoadingSeq(false);
      }
    }
    getSeq();
    return () => { ignore = true; };
  }, [draftDate, seqRefreshTick]);

  // ✅ 거래처 목록 불러오기
  useEffect(() => {
    async function fetchVendors() {
      setLoadingVendors(true);
      try {
        const res = await api.get("vendors/");
        const data = Array.isArray(res.data) ? res.data : res.data.results || [];
        setVendors(data);
      } catch (err) {
        console.error("❌ 거래처 목록 불러오기 실패:", err);
      } finally {
        setLoadingVendors(false);
      }
    }
    fetchVendors();
  }, []);

  // ✅ 거래처 자동완성 필터
  const filteredVendors = vendors.filter((v) =>
    v.name.toLowerCase().includes(vendorInput.toLowerCase())
  );

  // ✅ 거래처 입력 시
  const handleChangeVendor = (e) => {
    setVendorInput(e.target.value);
    setVendorId(null);
    setShowVendorList(true);
    setHighlightIndex(-1);
  };

  // ✅ 거래처 선택
  const handlePickVendor = (v) => {
    setVendorId(v.id);
    setVendorInput(v.name);
    setShowVendorList(false);
    setHighlightIndex(-1);
  };

  // ✅ 방향키 이동 및 Enter 선택
  const handleVendorKeyDown = (e) => {
    if (!showVendorList || filteredVendors.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex((prev) =>
        prev < filteredVendors.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((prev) =>
        prev > 0 ? prev - 1 : filteredVendors.length - 1
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightIndex >= 0 && filteredVendors[highlightIndex]) {
        handlePickVendor(filteredVendors[highlightIndex]);
      }
    }
  };

  // ✅ 제목 자동 생성
  useEffect(() => {
    if (vendorInput && subcategory && workMonth) {
      let year = "", month = "";

      if (workMonth.includes("-")) {
        [year, month] = workMonth.split("-");
      } else if (workMonth.includes("년")) {
        year = workMonth.split("년")[0].trim();
        month = workMonth.split("년")[1].replace("월", "").trim();
      }

      if (month && month.length === 1) month = "0" + month;

      // ✅ 거래처명에서 괄호() 제거
      const cleanVendor = vendorInput.replace(/\(.*?\)/g, "").trim();

      if (year && month) {
        setTitle(`${year}년 ${month}월 ${cleanVendor} ${subcategory} 청구서`);
      }
    } else {
      setTitle("");
    }
  }, [vendorInput, subcategory, workMonth]);

  // ✅ 저장 기능
  const onSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);

    if (!subcategory || !title || !amount || !vendorInput) {
      alert("필수 항목을 모두 입력해 주세요.");
      setSaving(false);
      return;
    }

    let safeWorkMonth = workMonth;
    if (safeWorkMonth.includes("년")) {
      safeWorkMonth = safeWorkMonth
        .replace("년 ", "-")
        .replace("월", "")
        .trim();
    }

    const payload = {
      category,
      subcategory,
      title,
      draft_date: draftDate,
      claim_date: claimDate || null,
      work_month: safeWorkMonth,
      partner: vendorId || null,
      partner_name: vendorInput,
      amount: amount ? Number(amount) : 0,
      pay_method: PAY_METHOD_TO_CODE[payMethodLabel] || "transfer",

      
      desc: desc || "",
      seq,
      draft_no: draftNo,
      aircond_details:
        subcategory === "냉난방기 종합세척" ? aircondDetails : [],
    };

    try {
      const { data } = await api.post(`/env-total-incomes/`, payload);
      setSavedId(data.id);
      alert(`✅ 저장 완료!\n거래처: ${vendorInput}\n금액: ${amount}원`);
    } catch (err) {
      console.error("❌ 저장 실패:", err.response?.data || err);
      alert("서버 저장 실패. 관리자에게 문의하세요.");
    } finally {
      setSaving(false);
    }
  };

  // ✅ 내보내기 기능
  const download = (fmt) => {
    if (!savedId) return alert("먼저 저장해 주세요.");
    window.open(`${api.defaults.baseURL}env-total-incomes/${savedId}/export?fmt=${fmt}`, "_blank");
  };

  return (
    <div className={styles.wrap}>
      <div style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>
        <div className={styles.pane} style={{ flex: 1 }}>
        {activeTab === "draft" && (
          <form className={styles.form} onSubmit={onSubmit}>
            <h3 style={{ marginBottom: "10px" }}>환경/종합 수입기안서 작성</h3>





            <Row label="기안번호(자동완성)">
              <input value={draftNo} readOnly />
            </Row>

            <Row label="제목(자동완성)">
              <input
                value={title}
                readOnly
                placeholder="청구일·작업월·거래처 입력 시 자동 생성됩니다."
              />
            </Row>

            <Row label="사업구분"><input value={category} readOnly /></Row>

            <Row label="세부구분">
              <select
                value={subcategory}
                onChange={(e) => setSubcategory(e.target.value)}
                className={styles.selectBox}
              >
                {SUBCATEGORY_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </Row>

            <Row label="작성일">
              <input type="date" value={draftDate} onChange={(e) => setDraftDate(e.target.value)} />
            </Row>

            <Row label="청구일">
              <input type="date" value={claimDate} onChange={(e) => setClaimDate(e.target.value)} />
            </Row>

            <Row label="작업월(귀속월)">
              <input type="month" value={workMonth} onChange={(e) => setWorkMonth(e.target.value)} />
            </Row>

            <Row label="거래처">
              <div className={styles.partnerRow}>
                <input
                  placeholder="직접 입력 또는 검색"
                  value={vendorInput}
                  onChange={handleChangeVendor}
                  onKeyDown={handleVendorKeyDown}
                />
                <button
                  type="button"
                  className={styles.partnerButton}
                  onClick={() => setShowVendorList((v) => !v)}
                >
                  ▼
                </button>
              </div>
              {showVendorList && (
                <div className={styles.partnerBox}>
                  {loadingVendors ? (
                    <div>불러오는 중...</div>
                  ) : filteredVendors.length > 0 ? (
                    filteredVendors.map((v, i) => (
                      <div
                        key={v.id}
                        className={`${styles.partnerItem} ${i === highlightIndex ? styles.activeItem : ""}`}
                        onClick={() => handlePickVendor(v)}
                      >
                        {v.name}
                      </div>
                    ))
                  ) : (
                    <div>일치하는 거래처가 없습니다.</div>
                  )}
                </div>
              )}
            </Row>

            <Row label="금액">
              <input
                inputMode="numeric"
                placeholder="숫자만"
                value={amount}
                onChange={(e) => setAmount(e.target.value.replace(/[^\d]/g, ""))}
              />
            </Row>

            <Row label="거래방법">
              <div className={styles.inline}>
                {PAY_METHOD_LABELS.map((label) => (
                  <label key={label} className={styles.check}>
                    <input
                      type="checkbox"
                      checked={payMethodLabel === label}
                      onChange={() => setPayMethodLabel(label)}
                    />
                    {label}
                  </label>
                ))}
              </div>
            </Row>

            <Row label="본문/비고">
              <textarea
                rows={4}
                value={desc}
                onChange={(e) => setDesc(e.target.value)}
                placeholder="청구 관련 메모를 입력하세요."
              />
            </Row>

            <div className={styles.actions}>
              <button type="submit" disabled={saving} className={styles.primaryBtn}>
                {saving ? "저장 중..." : "저장"}
              </button>
            </div>

            <div className={styles.actions}>
              <button type="button" onClick={() => download("docx")} className={styles.primaryBtn}>한글(DOCX)</button>
              <button type="button" onClick={() => download("pdf")} className={styles.primaryBtn}>PDF</button>
            </div>
          </form>
        )}
        {activeTab === "invoice" && <InvoiceForm />}
        {activeTab === "statement" && <StatementForm />}
        </div>

        {/* ✅ 냉난방기 종합세척 선택 시 오른쪽 별도 패널 표시 */}

        {activeTab === "draft" && subcategory === "냉난방기 종합세척" && (
          <div className={styles.pane} style={{ flex: 1 }}>
            <h3 style={{ marginBottom: "10px" }}>냉난방기 종합세척 상세내역</h3>

              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>

                <thead>
                  <tr style={{ background: "#f9fafb" }}> 
                    <th style={{ ...thStyle, width: "20%" }}>구분</th>                
                    <th style={{ ...thStyle, width: "12%" }}>대수(대)</th>        
                    <th style={{ ...thStyle, width: "25%" }}>단가(원)</th>
                    <th style={{ ...thStyle, width: "35%" }}>계약금액(원)</th>
                    <th style={{ ...thStyle, width: 36 }}></th>
                  </tr>
                </thead>

                <tbody>
                  {aircondDetails.length === 0 && (
                    <tr>
                      <td colSpan={5} style={{ ...tdStyle, textAlign: "center", color: "#9ca3af", padding: "16px" }}>
                        아래 버튼으로 구분을 추가하세요.
                      </td>
                    </tr>
                  )}
                  {aircondDetails.map((row, i) => (
                    <tr key={i}>
                      <td style={tdStyle}>
                        <input
                          type="text"
                          placeholder="예: 벽걸이형"
                          value={row.aircond_type}
                          onChange={(e) => handleAircondChange(i, "aircond_type", e.target.value)}
                          style={{ ...cellInputStyle, textAlign: "center" }}
                        />
                      </td>
                      <td style={tdStyle}>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={row.quantity}
                          onChange={(e) => handleAircondChange(i, "quantity", e.target.value)}
                          style={cellInputStyle}
                        />
                      </td>
                      <td style={tdStyle}>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={row.unit_price}
                          onChange={(e) => handleAircondChange(i, "unit_price", e.target.value)}
                          style={cellInputStyle}
                        />
                      </td>
                      <td style={{ ...tdStyle, textAlign: "right", background: "#f9fafb" }}>
                        {row.contract_amount.toLocaleString()}
                      </td>
                        <td style={{ ...tdStyle, textAlign: "center", padding: "0 4px" }}>
                        {!row.isDefault && (
                          <button
                            type="button"
                            onClick={() => handleRemoveAircondRow(i)}
                            style={{ background: "none", border: "none", color: "#ef4444", cursor: "pointer", fontSize: 16 }}
                            title="행 삭제"
                          >
                            ✕
                          </button>
                        )}
                      </td>


                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={5} style={{ padding: "6px 8px" }}>
                      <button
                        type="button"
                        onClick={handleAddAircondRow}
                        style={{ width: "17%", padding: "6px", border: "1px dashed #94a3b8", borderRadius: 4, background: "#f8fafc", color: "#475569", cursor: "pointer", fontSize: 13 }}

                      >
                        + 구분 추가
                      </button>
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={3} style={{ ...tdStyle, textAlign: "center", fontWeight: 600, background: "#f1f5f9" }}>
                      합계
                    </td>
                    <td colSpan={2} style={{ ...tdStyle, textAlign: "right", fontWeight: 700, background: "#f1f5f9" }}>
                      {aircondTotal.toLocaleString()} 원
                    </td>
                  </tr>

                  
                </tfoot>
              </table>
              <p style={{ fontSize: 12, color: "#6b7280", marginTop: 8 }}>
                ※ 계약금액은 대수 × 단가로 자동 계산됩니다. 합계는 좌측 "금액"에 자동 반영됩니다.
              </p>
          </div>
        )}




      </div>
    </div>
  );  
}


// ✅ 공통 Row
function Row({ label, children }) {
  return (
    <div className={styles.row}>
      <div className={styles.rowLabel}>{label}</div>
      <div className={styles.rowField}>{children}</div>
    </div>
  );
}

// ✅ 날짜 포맷 함수
function toISO(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// ✅ 상세내역 테이블 스타일
const thStyle = {
  border: "1px solid #d1d5db",
  padding: "8px",
  fontWeight: 600,
  textAlign: "center",
};
const tdStyle = {
  border: "1px solid #d1d5db",
  padding: "4px 8px",
  verticalAlign: "middle",
};
const cellInputStyle = {
  width: "100%",
  border: "none",
  outline: "none",
  padding: "4px",
  textAlign: "right",
  background: "transparent",
};

